import { Container, Card, CardHeader, Heading, CardBody, Divider, FormControl, Input, FormLabel, CardFooter, Button, useToast, Flex, Box, Image, Tag } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "store/store.hook";
import { useEffect, useState } from "react";
import { SubTarget } from "types/SubTarget";
import MyBreadcrumb from "components/breadcrumb/Breadcrumb";
import { useForm } from "react-hook-form";
import { updateSelectedDocumentIndicators } from "store/reducers/documents.slice";
import { patchIndicatorValues } from "service/api/document.api";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { checkIfIndicatorIsInRolesArray, explodeIndicatorId } from "utils/utils";

type IndicatorProps = {
  isReadOnly?: boolean;
};


/**
 * Indicator component that displays and manages the state of an indicator.
 * 
 * @param {IndicatorProps} props - The properties for the Indicator component.
 * @param {boolean} [props.isReadOnly=true] - Determines if the indicator is read-only.
 * 
 * @returns {JSX.Element} The rendered Indicator component.
 * 
 * @component
 * 
 * @example
 * <Indicator isReadOnly={false} />
 * 
 * @remarks
 * This component uses various hooks from React and Redux to manage state and side effects.
 * It also uses the `useForm` hook from `react-hook-form` to handle form state and submission.
 * 
 * @requires useNavigate
 * @requires useAppDispatch
 * @requires useLocation
 * @requires useSelector
 * @requires useParams
 * @requires useToast
 * @requires useForm
 * 
 * @function
 * @name Indicator
 * 
 * @hook
 * @name useEffect
 * @description Sets the `isAllowed` state based on user roles and admin status.
 * 
 * @hook
 * @name useEffect
 * @description Calculates and sets the totals for departments and central administration.
 * 
 * @param {Object} data - The form data submitted by the user.
 * @param {Function} onSubmit - Handles form submission and updates the indicator values.
 * 
 * @param {string} id - The identifier for the indicator.
 * @param {Function} updateTotals - Updates the totals for departments and central administration.
 * 
 * @param {Function} handleBack - Navigates back to the previous page.
 * 
 * @param {Function} retrieveDefaultValues - Retrieves the default values for the form fields.
 * 
 * @param {Object} defaultValues - The default values for the form fields.
 * 
 * @param {Object} totalsDepartments - The totals for the departments.
 * @param {Object} totalsCentralAdministration - The totals for the central administration.
 * 
 * @param {boolean} isAllowed - Determines if the user is allowed to modify the indicator.
 * 
 * @param {Object} selectedDocument - The selected document from the Redux store.
 * @param {Object} indicator - The indicator object from the selected document.
 * 
 * @param {string} targetId - The ID of the target.
 * @param {string} subtargetId - The ID of the subtarget.
 * @param {string} actionId - The ID of the action.
 * @param {string} indicatorId - The ID of the indicator.
 * 
 * @param {Array} divisions - The list of divisions from the Redux store.
 * @param {Array} attributesSdg - The list of SDG attributes from the Redux store.
 * @param {Array} attributesPnrr - The list of PNRR attributes from the Redux store.
 * @param {Array} attributesAnvur - The list of ANVUR attributes from the Redux store.
 * 
 * @param {Object} toast - The toast object for displaying notifications.
 */
export default function Indicator({ isReadOnly = true }: IndicatorProps) {

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const roles = useSelector((state: any) => state.user.user?.roles);
  const isUserAdmin = useSelector((state: any) => state.user.user?.isAdmin);


  const params = useParams();
  const { targetIndex, subtargetIndex, actionIndex, indicatorIndex, timePeriod } = params;
  const selectedDocumentId = useSelector((state: any) => state.documents.selectedDocumentId);
  const selectedDocument = (useSelector((state: any) => state.documents.documents.find((doc: any) => doc._id === selectedDocumentId)));
  const indicator = selectedDocument.targets.find((target: any) => target.index === targetIndex).subtargets.find((subtarget: SubTarget) => subtarget.index === subtargetIndex).actions.find((action: any) => action.index === actionIndex).indicators.find((indicator: any) => indicator.index === indicatorIndex);
  const targetId: string = selectedDocument.targets.find((target: any) => target.index === targetIndex)._id;
  const subtargetId: string = selectedDocument.targets.find((target: any) => target.index === targetIndex).subtargets.find((subtarget: SubTarget) => subtarget.index === subtargetIndex)._id;
  const actionId: string = selectedDocument.targets.find((target: any) => target.index === targetIndex).subtargets.find((subtarget: SubTarget) => subtarget.index === subtargetIndex).actions.find((action: any) => action.index === actionIndex)._id;
  const indicatorId: string = indicator._id;
  const divisions = useSelector((state: any) => state.divisions.divisions);
  const attributesSdg = useSelector((state: any) => state.attributes.attributesSdg);
  const attributesPnrr = useSelector((state: any) => state.attributes.attributesPnrr);
  const attributesAnvur = useSelector((state: any) => state.attributes.attributesAnvur);
  const toast = useToast();

  const [isAllowed, setIsAllowed] = useState(false);

  const [totalsDepartments, setTotalsDepartments] = useState<any>({});
  const [totalsCentralAdministration, setTotalsCentralAdministration] = useState<any>({});

  useEffect(() => {
    setIsAllowed(checkIfIndicatorIsInRolesArray(selectedDocumentId, subtargetId, indicatorId, roles, true, isUserAdmin));
  }, [])

  const retrieveDefaultValues =
    () => {
      let defaultValues: any = {};
      const target = selectedDocument?.targets.find((target: any) => target._id === targetId);
      const subtarget = target?.subtargets.find((subtarget: any) => subtarget._id === subtargetId);
      subtarget?.actions.map((action: any) => {
        action?.indicators.map((indicator: any) => {
          divisions.map((departmemnt: any) => {

            let valuesToSet = '';
            if (indicator?.values && timePeriod in indicator?.values) {
              valuesToSet = indicator?.values[timePeriod]?.[departmemnt.code] ?? '';
            }

            defaultValues[`indicator-${targetId}-${subtargetId}-${action?._id}-${indicator?._id}-${departmemnt._id}`] = valuesToSet;
          })
        })
      })
      return defaultValues;
    }

  const { register, handleSubmit, formState, reset, getValues } = useForm({
    defaultValues: retrieveDefaultValues(),
  });

  useEffect(() => {


    let defaultTotalsDepartments: any = {};


    const sumArr1 = divisions.map((department: any) => {
      if (department.isDepartment) {
        const val = getValues(`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}-${department._id}`);
        if (!val) {
          return 0;
        } else {
          return parseFloat(val);
        }
      }
    });

    //sum all the values of the departments
    let sum1 = 0;
    for (let i = 0; i < sumArr1.length; i++) {
      //sum only if is a number
      if (!isNaN(sumArr1[i]))
        sum1 += sumArr1[i];
    }

    defaultTotalsDepartments[`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}`] = sum1;



    let defaultTotalsCentralAdministration: any = {};

    const sumArr = divisions.map((department: any) => {
      if (department.isCentralAdministration) {
        const val = getValues(`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}-${department._id}`);
        if (!val) {
          return 0;
        } else {
          return parseFloat(val);
        }
      }
    });

    //sum all the values of the departments
    let sum = 0;
    for (let i = 0; i < sumArr.length; i++) {
      //sum only if is a number
      if (!isNaN(sumArr[i]))
        sum += sumArr[i];
    }

    defaultTotalsCentralAdministration[`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}`] = sum;

    setTotalsDepartments(defaultTotalsDepartments);
    setTotalsCentralAdministration(defaultTotalsCentralAdministration);

  }, [subtargetId, divisions, timePeriod]);

  const updateTotals = (id: string) => {

    const { targetId, subTargetId, actionId, indicatorId } = explodeIndicatorId(id);

    const sumCentralAdministration = divisions.reduce((acc: number, department: { isCentralAdministration: any; _id: any; code: any }) => {
      if (department.isCentralAdministration) {

        let value = parseFloat(getValues(`indicator-${targetId}-${subTargetId}-${actionId}-${indicatorId}-${department._id}`));

        if (isNaN(value)) {
          value = 0;
        }

        return acc + value;
      }
      return acc;

    }, 0);

    const sumDepartments = divisions.reduce((acc: number, department: { isDepartment: any; _id: any; code: any }) => {
      if (department.isDepartment) {

        let value = parseFloat(getValues(`indicator-${targetId}-${subTargetId}-${actionId}-${indicatorId}-${department._id}`));

        if (isNaN(value)) {
          value = 0;
        }

        return acc + value;
      }
      return acc;
    }, 0);

    setTotalsCentralAdministration((prevTotals: any) => ({
      ...prevTotals,
      [`indicator-${targetId}-${subTargetId}-${actionId}-${indicatorId}`]: sumCentralAdministration
    }));

    setTotalsDepartments((prevTotals: any) => ({
      ...prevTotals,
      [`indicator-${targetId}-${subTargetId}-${actionId}-${indicatorId}`]: sumDepartments
    }));
  };

  const onSubmit = (data: any) => {

    let newValues: any = {};

    divisions.map((source: any) => {
      newValues[source.code] = data[`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}-${source._id}`] ?? "";
    })

    patchIndicatorValues(selectedDocumentId, targetId, subtargetId, actionId, indicatorId, timePeriod, newValues).then((res: any) => {

      dispatch(
        updateSelectedDocumentIndicators({ indicators: data, timePeriod: timePeriod, divisions: divisions })
      )

      toast({
        title: "Modifiche salvate",
        status: "success",
        duration: 9000,
        isClosable: true,
        position: "top",
      });
    }).catch((err: any) => {
      toast({
        title: "Errore",
        description: "Errore durante il salvataggio delle modifiche",
        status: "error",
        duration: 9000,
        isClosable: true,
        position: "top",
      });
    })
  };

  function handleBack() {
    navigate(-1);
  }



  return (


    <section className="dashboard-first-section">
      <Container maxW="container.lg" maxH={"container.xl"} textAlign={"left"}>
        <MyBreadcrumb />
        <form onSubmit={handleSubmit(onSubmit)}>

          <Card minH={"60vh"}>
            <CardHeader>

              <Link to={`/targets/${targetIndex}/${isReadOnly ? "view" : "edit"}`}
                state={{
                  targetId: targetId,
                  subTargetId: subtargetId,
                  actionId: actionId,
                  indicatorId: indicatorId,
                  timePeriod: timePeriod,
                }}>
                <Button onClick={handleBack} as="a" aria-label="Torna alla pagina precedente" cursor={"pointer"} size={"xs"}>
                  <ArrowBackIcon />
                  Torna alla pagina precedente
                </Button>
              </Link>

              <Heading as="h1" size="xl" >
                Indicatore {indicatorIndex} - {indicator?.title}
              </Heading>
              {
                isAllowed == false &&
                <>
                  <Tag colorScheme='red' >Indicatore non modificabile 🔒</Tag>
                </>
              }

              {
                <>
                  <Divider orientation='horizontal' mt={1} />

                  <Flex flexDir={"row-reverse"}>
                    <Card size={"sm"} p={0} variant={"none"} shadow={"none"}>
                      <CardBody display={"inline-block"} p={1}>

                        <Flex justifyContent={"space-between"}>
                          {
                            attributesSdg?.map((item: any) => {
                              return indicator?.sdg?.includes(item.code) &&
                                <Image src={require(`assets/img/sdg-icons/${item.icon}`)} alt={item.title} title={item.title} boxSize="56px" display={"inline"} mx={1} />
                            })
                          }
                          {
                            ((indicator?.pnrr?.length > 0) && (indicator?.sdg?.length > 0)) &&
                            <Box mx={1}>
                              <Divider orientation='vertical' />
                            </Box>
                          }
                          {
                            attributesPnrr?.map((item: any) => {
                              return indicator?.pnrr?.includes(item.code) &&
                                <Image src={require(`assets/img/pnrr-icons/${item.icon}`)} alt={item.title} title={item.title} boxSize="56px" display={"inline"} mx={1} />
                            })
                          }
                          {
                            ((indicator?.anvur?.length > 0) && (indicator?.pnrr?.length > 0)) &&
                            <Box mx={1}>
                              <Divider orientation='vertical' />
                            </Box>
                          }
                          {
                            attributesAnvur?.map((item: any) => {
                              return indicator?.anvur?.includes(item.code) &&
                                <Image src={require(`assets/img/anvur-icons/${item.icon}`)} alt={item.title} title={item.title} boxSize="56px" display={"inline"} mx={1} />
                            })
                          }
                          {
                            indicator?.strategicPlan &&
                            <Tag colorScheme='blue' mx={1}>Piano strategico</Tag>
                          }
                        </Flex>
                      </CardBody>
                    </Card>
                  </Flex>
                  <Divider orientation='horizontal' />
                </>
              }
              {
                isAllowed == false &&
                <>
                  <Tag colorScheme='red'>Indicatore non modificabile 🔒</Tag>
                </>
              }

            </CardHeader>
            <CardBody>
              {divisions.map((department: any, index: number) => {
                return (

                  <>

                    <FormControl id="department" key={`department-${index}`} mb={4}>
                      <FormLabel htmlFor={`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}-${department._id}`}
                        fontSize={"smaller"} my={0}>{department.title} ({department.code})</FormLabel>
                      <Input
                        id={`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}-${department._id}`}
                        placeholder={indicator?.placeholder}
                        size="sm"
                        bgColor={"white"}
                        readOnly={isReadOnly}
                        pattern="(\d*|np)"
                        title="Inserisci un numero o la stringa 'NP'"
                        onKeyUp={() => updateTotals(`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}`)}
                        {
                        ...register(`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}-${department._id}`)
                        }
                      />
                    </FormControl>
                  </>

                )
              })}

              <Flex flexDir={"row-reverse"} mt={2}>
                <Tag colorScheme='gray'>
                  Totale AC+CAB+CAM:
                  {
                    totalsCentralAdministration && totalsCentralAdministration[`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}`] ? totalsCentralAdministration[`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}`] : 0
                  }
                </Tag>

                <Tag colorScheme='gray' mr={2}>
                  Totale Dipartimenti:
                  {
                    totalsDepartments && totalsDepartments[`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}`] ? totalsDepartments[`indicator-${targetId}-${subtargetId}-${actionId}-${indicatorId}`] : 0
                  }
                </Tag>
              </Flex>
            </CardBody >
            <CardFooter flexDir={"row-reverse"}  >
              {
                isReadOnly == false && isAllowed &&
                (
                  <Button colorScheme="green" mr={3} type="submit" >
                    Salva modifiche
                  </Button>
                )
              }
              <Button onClick={handleBack} mr={5} as="a" aria-timePeriod="Torna alla pagina precedente" cursor={"pointer"}>
                <ArrowBackIcon />
                Torna alla pagina precedente
              </Button>

            </CardFooter>
          </Card>
        </form>
      </Container>
    </section>

  )

}
